<script setup lang="ts">
import type { FinancingsList, UseFinancingsParams } from '~/store-v2/financings/useFinancings'
import type { ComboTypeFilter } from '~/utils/financing-flow/comboType'
import type { FilterField, FilterOptions, SelectedFilters } from '~/utils/financing-flow/Financing'
import IconSearch from '~icons/girassol/search'
import IconClear from '~icons/material-symbols/delete'
import { STATUSES_MAPPING } from '~/services-v2/financial-bff/financial-bff.consts'
import { COMBO_TYPE_FILTER, comboTypeFromParams, isComboTypeFilter } from '~/utils/financing-flow/comboType'
import { STEP_STATUS_MAPPING } from '~/utils/financing-flow/Financing'

const props = defineProps<{
  initialValue: UseFinancingsParams
  financings: FinancingsList
  hasActiveQuickFilter?: boolean
}>()

const emit = defineEmits<{
  (e: 'applyFilters', value?: SelectedFilters & { query?: string }): void
  (e: 'applyComboType', value: ComboTypeFilter): void
  (e: 'clearFilters'): void
}>()

const { t } = useI18n()

const filters = reactive<SelectedFilters>({
  steps: [],
  status: [],
})

const query = ref('')

const comboType = computed(() => comboTypeFromParams(props.initialValue))
const hasComboType = computed(() => comboType.value !== COMBO_TYPE_FILTER.ALL)

// No "all" option: an empty select (placeholder) means no filter, like Etapa and Status
const comboTypeOptions = computed<FilterField[]>(() => Object.values(COMBO_TYPE_FILTER)
  .filter(type => type !== COMBO_TYPE_FILTER.ALL)
  .map(type => ({
    value: type,
    name: t(`financing.filters.combo_type.${type}`),
  })))
const selectedComboType = computed(() => comboTypeOptions.value.find(option => option.value === comboType.value))

function handleSelectComboType(value: FilterField | Array<FilterField>) {
  const selected = Array.isArray(value) ? value[0] : value
  emit('applyComboType', isComboTypeFilter(selected?.value) ? selected.value : COMBO_TYPE_FILTER.ALL)
}

const optionsFilters = reactive<FilterOptions>({
  steps: [],
  status: [],
})

function filterStatusBySteps(selectedSteps: FilterField[], selectedStatus: FilterField[]): FilterField[] {
  if (selectedSteps.length === 0) {
    return selectedStatus
  }

  const validStatusSet = new Set<string>()
  selectedSteps.forEach((step) => {
    const validStatuses = STEP_STATUS_MAPPING[step.value] || []
    validStatuses.forEach(status => validStatusSet.add(status))
  })

  return selectedStatus.filter(status => validStatusSet.has(status.value))
}

function applyFilters() {
  const filteredStatus = filterStatusBySteps(
    Array.isArray(filters.steps) ? filters.steps : [],
    Array.isArray(filters.status) ? filters.status : [],
  )

  emit('applyFilters', {
    ...filters,
    status: filteredStatus,
    query: query.value,
  })
}

function handleSelectFilter(field: keyof SelectedFilters, value: FilterField | Array<FilterField>) {
  if (field === 'status') {
    filters.status = (Array.isArray(value) ? value : (value ? [value] : [])) as FilterField[]
  }
  else if (field === 'steps') {
    const newSteps = Array.isArray(value)
      ? (value as FilterField[])
      : (value ? [value] : [])

    filters.steps = newSteps

    if (newSteps.length > 0) {
      filters.status = filterStatusBySteps(newSteps, Array.isArray(filters.status) ? filters.status : [])
    }
  }

  applyFilters()
}

// Last search term received from the params: a typed-but-not-applied search survives when only another param changes (e.g. the combo type)
let lastInitialQuery: string | undefined

function initializeFilters() {
  if (optionsFilters.steps.length === 0) {
    return
  }

  if (props.initialValue.steps && props.initialValue.steps.length > 0) {
    const initialStepsValues = props.initialValue.steps.map(s => String(s))
    const matchedSteps = optionsFilters.steps.filter(option =>
      initialStepsValues.includes(option.value),
    )
    filters.steps = matchedSteps.length > 0 ? [matchedSteps[0]] : []
  }
  else {
    filters.steps = []
  }

  if (props.initialValue.status && props.initialValue.status.length > 0) {
    const initialStatusValues = props.initialValue.status.map(s => String(s))
    filters.status = optionsFilters.status.filter(option =>
      initialStatusValues.includes(option.value),
    )
  }
  else {
    filters.status = []
  }

  const initialQuery = props.initialValue.query || ''
  if (initialQuery !== lastInitialQuery) {
    query.value = initialQuery
    lastInitialQuery = initialQuery
  }
}

function clearFilters() {
  filters.steps = []
  filters.status = []
  query.value = ''
  emit('clearFilters')
}

watch(
  () => props.financings,
  (financings) => {
    if (!financings)
      return

    optionsFilters.steps = financings.all_steps || []

    optionsFilters.status = (financings.statuses || []).map(status => ({
      value: String(status),
      name: t(STATUSES_MAPPING[status || '']?.textKey || status),
    }))

    initializeFilters()
  },
  { immediate: true },
)

watch(
  () => props.initialValue,
  () => {
    if (optionsFilters.steps.length > 0) {
      initializeFilters()
    }
  },
  { deep: true },
)
</script>

<template>
  <div class="box-container">
    <div class="flex flex-col md:system:flex-row gap-3">
      <SolInputText
        id="searchFinancing"
        v-model="query"
        name="searchFinancing"
        :placeholder="t('financing.boxSearch.placeholder')"
        type="text"
        class="w-full md:system:min-w-[320px]"
        @keyup.enter="applyFilters()"
      >
        <template #icon>
          <IconSearch />
        </template>
      </SolInputText>

      <SolButton
        id="buttonSearch"
        size="medium"
        variant="secondary"
        class="w-full md:system:w-[100px]"
        @click="applyFilters()"
      >
        <template #icon-left>
          <IconSearch />
        </template>
        {{ t('financing.boxSearch.button') }}
      </SolButton>
    </div>

    <SolSelect
      id="step-filter"
      name="step-filter"
      class="w-full md:system:w-auto min-w-[200px] h-12"
      placeholder="Etapa"
      size="medium"
      :options="optionsFilters.steps"
      :selected="Array.isArray(filters.steps) && filters.steps.length > 0 ? filters.steps[0] : undefined"
      @update:selected="value => handleSelectFilter('steps', value as FilterField | Array<FilterField>)"
    />

    <SolSelect
      id="status-filter"
      name="status-filter"
      class="filter-select-step flex-1"
      placeholder="Status"
      size="medium"
      :multiple="true"
      :options="optionsFilters.status"
      :selected="Array.isArray(filters.status) ? filters.status : []"
      @update:selected="value => handleSelectFilter('status', value as FilterField | Array<FilterField>)"
    />
    <SolSelect
      id="combo-type-filter"
      name="combo-type-filter"
      class="w-full md:system:w-auto min-w-[200px] h-12"
      :placeholder="t('financing.filters.combo_type.label')"
      :aria-label="t('financing.filters.combo_type.label')"
      size="medium"
      :options="comboTypeOptions"
      :selected="selectedComboType"
      @update:selected="value => handleSelectComboType(value as FilterField | Array<FilterField>)"
    />
    <button
      v-if="query || (Array.isArray(filters.status) ? filters.status.length > 0 : filters.status) || (Array.isArray(filters.steps) ? filters.steps.length > 0 : filters.steps) || hasComboType || hasActiveQuickFilter"
      class="clear-button"
      @click="clearFilters"
    >
      <IconClear class="w-4 h-4" />
      {{ t('financing.filters.clear', [query, filters.status, filters.steps, hasComboType ? comboType : ''].filter(item => Array.isArray(item) ? item.length > 0 : Boolean(item)).length >= 2 ? 0 : 1) }}
    </button>
  </div>
</template>

<style scoped lang="scss">
.box-container {
  @apply mb-2xs mt-xs flex flex-col gap-3 w-full flex-wrap;

  @screen md:system {
    @apply flex-row;
  }

  .filter-select-step {
    @apply w-full md:system:w-auto min-w-[200px];

    :deep(.container-tags) {
      @apply flex-wrap;
    }
  }

  .clear-button {
    @apply text-feedback-negative-pure fonts-body-medium-bold flex gap-1 items-center;
    @apply hover:underline;
  }
}
</style>
